import React, { useEffect, useMemo, useState } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useAuth } from "./Auth/AuthContext";
import { useTheme } from "./ThemeContext";
import { useTranslation } from "react-i18next";
import {
  FaUserCircle,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaBell,
  FaRegCircle,
  FaTachometerAlt,
  FaTruckMoving,
  FaFileInvoice,
  FaFileAlt,
  FaUsers,
  FaCalendarAlt,
  FaBoxes,
  FaChevronLeft,
  FaChevronRight,
  FaSun,
  FaMoon,
  FaCog,
  FaChartLine,
  FaTruckLoading,
  FaBuilding,
  FaUserShield,
  FaEnvelope,
} from "react-icons/fa";
import { useCompany } from "./Company/CompanyContext";
import api from "../api";
import { isOrgAdmin, isSuperadmin } from "../utils/roles";

const NAV_LINKS = [
  { path: "/dashboard",  labelKey: "navbar.dashboard",  perm: "dashboard",    icon: <FaTachometerAlt /> },
  { path: "/missions",   labelKey: "navbar.missions",   perm: "missions",     icon: <FaTruckMoving /> },
  { path: "/quotes",     labelKey: "navbar.quotes",     perm: "quote",        icon: <FaFileAlt /> },
  { path: "/catalogue",  labelKey: "navbar.catalogue",  perm: "quote",        icon: <FaBoxes /> },
  { path: "/invoices",   labelKey: "navbar.invoices",   perm: "invoices",     icon: <FaFileInvoice /> },
  { path: "/investors",  labelKey: "navbar.investors",  perm: "investors",    icon: <FaUsers /> },
  { path: "/salaries",   labelKey: "navbar.salaries",   perm: "salaries",     icon: <FaRegCircle /> },
  { path: "/calendar",   labelKey: "navbar.calendar",   perm: "appointments", icon: <FaCalendarAlt /> },
  { path: "/subcontractors", labelKey: "navbar.subcontractors", perm: "subcontractors", icon: <FaTruckLoading /> },
  { path: "/performance", labelKey: "navbar.performance", perm: "performance", icon: <FaChartLine />, adminOnly: true },
  // Administration (gated par rôle) — dans la sidebar.
  { path: "/users",    labelKey: "navbar.manageUsers", icon: <FaUserShield />, adminOnly: true },
  { path: "/settings", labelKey: "navbar.settings",    icon: <FaCog />,        adminOnly: true },
  { path: "/admin/organizations", labelKey: "navbar.organizations", icon: <FaBuilding />, superadminOnly: true },
];

// Décide si un lien de nav est visible pour l'utilisateur courant.
function canSeeLink(link, user, permissions) {
  if (link.superadminOnly) return isSuperadmin(user);
  if (link.adminOnly) return isOrgAdmin(user);
  return permissions.includes(link.perm);
}

export default function Layout() {
  const location = useLocation();
  const { user, logout, impersonating, impersonatedOrg, stopImpersonation } = useAuth();
  const { isDark, toggle: toggleTheme } = useTheme();
  const { i18n, t } = useTranslation();
  const { company, logoPath } = useCompany();

  const companyName = company?.name || "The Movers";
  const logoSrc = logoPath
    ? `${api.defaults.baseURL}${logoPath}`
    : "/logo.jpg";

  const [drawerOpen, setDrawerOpen]           = useState(false);
  const [notifs, setNotifs]                   = useState([]);
  const [collapsed, setCollapsed]             = useState(() => {
    try { return JSON.parse(localStorage.getItem("sidebarCollapsed") || "false"); } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem("sidebarCollapsed", JSON.stringify(collapsed)); } catch {}
  }, [collapsed]);

  const canNotif = !!user && (isOrgAdmin(user) || (user.permissions || []).includes("notifications"));

  useEffect(() => {
    if (!canNotif) return setNotifs([]);
    const fetchNotifs = () => {
      api.get("/notifications").then(res => setNotifs(res.data)).catch(() => setNotifs([]));
    };
    fetchNotifs();
    const handleNotifUpdate = () => fetchNotifs();
    window.addEventListener("notification-update", handleNotifUpdate);
    return () => window.removeEventListener("notification-update", handleNotifUpdate);
  }, [canNotif]);

  const unreadCount = useMemo(() => (notifs || []).filter(n => !n.is_read).length, [notifs]);

  const permissions = user
    ? isOrgAdmin(user)
      ? NAV_LINKS.map(l => l.perm)
      : user.permissions || []
    : [];

  const switchLanguage = () => i18n.changeLanguage(i18n.language === "fr" ? "en" : "fr");

  if (location.pathname === "/login") return <Outlet />;

  return (
    <div className="h-screen w-full flex flex-col overflow-hidden bg-base-200">
      {/* ── Header ── */}
      <header className="sticky top-0 z-40 flex items-center justify-between bg-base-100/90 backdrop-blur-md border-b border-base-200 px-4 h-14 shrink-0 shadow-sm">
        {/* Left */}
        <div className="flex items-center gap-3">
          <button
            className="btn btn-ghost btn-sm btn-square md:hidden text-base-content/50"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
          >
            <FaBars className="text-base" />
          </button>
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl overflow-hidden border border-base-300 shadow-sm shrink-0">
              <img src={logoSrc} alt={companyName} className="w-full h-full object-contain" />
            </div>
            <span className="hidden sm:block font-extrabold text-lg tracking-tight text-base-content group-hover:text-primary transition-colors">
              {companyName}
            </span>
            <span className="hidden sm:block text-[10px] font-bold tracking-widest text-primary/70 uppercase bg-primary/8 border border-primary/15 rounded-md px-1.5 py-0.5">
              v2
            </span>
          </Link>
        </div>

        {/* Right */}
        <div className="flex items-center gap-1">
          {/* Dark mode toggle */}
          <button
            onClick={toggleTheme}
            className="btn btn-ghost btn-sm btn-circle text-base-content/50 hover:text-base-content"
            title={isDark ? "Mode clair" : "Mode sombre"}
          >
            {isDark ? <FaSun className="text-amber-400 text-base" /> : <FaMoon className="text-base" />}
          </button>

          {/* Language */}
          <button
            onClick={switchLanguage}
            className="btn btn-ghost btn-sm btn-circle text-base text-base-content/50 hover:text-base-content"
            title="Changer de langue"
          >
            {i18n.language === "fr" ? "🇫🇷" : "🇬🇧"}
          </button>

          {/* Notifications */}
          {canNotif && (
            <Link to="/notifications" className="btn btn-ghost btn-sm btn-circle relative text-base-content/50 hover:text-base-content">
              <FaBell className="text-base" />
              {unreadCount > 0 && (
                <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-error text-error-content text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Link>
          )}

          {/* User menu */}
          {user && (
            <div className="dropdown dropdown-end">
              <label
                tabIndex={0}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg cursor-pointer hover:bg-base-200 transition-colors"
              >
                <div className="w-7 h-7 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary text-xs font-bold uppercase shrink-0">
                  {user.username?.[0] || "U"}
                </div>
                <span className="hidden sm:block text-sm font-semibold text-base-content max-w-[110px] truncate">
                  {user.username}
                </span>
              </label>
              <ul
                tabIndex={0}
                className="dropdown-content menu menu-sm bg-base-100 rounded-xl shadow-xl border border-base-200 w-52 mt-2 z-50 p-1.5"
              >
                <li className="px-3 py-1.5 mb-1">
                  <div className="flex items-center gap-2 cursor-default">
                    <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary text-sm font-bold uppercase shrink-0">
                      {user.username?.[0] || "U"}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-base-content truncate">{user.username}</div>
                      <div className="text-xs text-base-content/40">{isOrgAdmin(user) ? t("navbar.roleAdmin") : t("navbar.roleUser")}</div>
                    </div>
                  </div>
                </li>
                <div className="h-px bg-base-200 mx-1 mb-1" />
                <li>
                  <Link to="/profile" className="gap-2 text-base-content/70 hover:text-base-content rounded-lg">
                    <FaUserCircle className="text-base-content/40" /> {t("navbar.profile")}
                  </Link>
                </li>
                {/* Utilisateurs / Paramètres / Organisations sont désormais dans la sidebar. */}
                <div className="h-px bg-base-200 mx-1 my-1" />
                <li>
                  <button onClick={logout} className="gap-2 text-error hover:bg-error/10 rounded-lg">
                    <FaSignOutAlt /> {t("navbar.logout")}
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </header>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Sidebar desktop ── */}
        <aside
          className={`hidden md:flex flex-col shrink-0 ${
            collapsed ? "w-[68px]" : "w-64"
          } border-r border-base-200 bg-base-100 transition-all duration-200 overflow-hidden`}
        >
          <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
            {NAV_LINKS.filter(link => canSeeLink(link, user, permissions)).map(({ path, labelKey, icon }) => (
              <NavLink
                key={path}
                to={path}
                title={collapsed ? t(labelKey) : undefined}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 px-3 py-3 rounded-lg text-[15px] font-medium transition-all duration-150 ${
                    collapsed ? "justify-center" : ""
                  } ${
                    isActive
                      ? "bg-primary/10 text-primary font-semibold"
                      : "text-base-content/50 hover:bg-base-200 hover:text-base-content"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && !collapsed && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-primary rounded-full" />
                    )}
                    <span className={`text-xl shrink-0 ${isActive ? "text-primary" : ""}`}>{icon}</span>
                    {!collapsed && <span className="truncate">{t(labelKey)}</span>}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="p-2 border-t border-base-200">
            <button
              onClick={() => setCollapsed(v => !v)}
              className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium text-base-content/30 hover:text-base-content/60 hover:bg-base-200 transition-colors ${
                collapsed ? "justify-center" : ""
              }`}
              title={collapsed ? "Développer" : "Réduire"}
            >
              {collapsed ? <FaChevronRight className="text-xs" /> : (
                <>
                  <FaChevronLeft className="text-xs" />
                  <span>Réduire</span>
                </>
              )}
            </button>
          </div>
        </aside>

        {/* ── Drawer mobile ── */}
        {drawerOpen && (
          <div className="md:hidden fixed inset-0 z-50 flex">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
            <aside className="relative z-10 w-64 bg-base-100 shadow-2xl flex flex-col h-full">
              <div className="flex items-center justify-between px-4 py-3.5 border-b border-base-200">
                <Link to="/" className="flex items-center gap-2" onClick={() => setDrawerOpen(false)}>
                  <img src={logoSrc} alt={companyName} className="w-7 h-7 rounded-lg object-contain border border-base-300" />
                  <span className="font-extrabold text-base-content">{companyName}</span>
                </Link>
                <button className="btn btn-ghost btn-sm btn-square text-base-content/40" onClick={() => setDrawerOpen(false)}>
                  <FaTimes />
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
                {NAV_LINKS.filter(link => canSeeLink(link, user, permissions)).map(({ path, labelKey, icon }) => (
                  <NavLink
                    key={path}
                    to={path}
                    onClick={() => setDrawerOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                        isActive
                          ? "bg-primary/10 text-primary font-semibold"
                          : "text-base-content/50 hover:bg-base-200 hover:text-base-content"
                      }`
                    }
                  >
                    <span className="text-base">{icon}</span>
                    <span>{t(labelKey)}</span>
                  </NavLink>
                ))}
              </nav>

              <div className="p-3 border-t border-base-200 space-y-1">
                <button
                  onClick={toggleTheme}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-base-content/60 hover:bg-base-200"
                >
                  {isDark ? <FaSun className="text-amber-400" /> : <FaMoon />}
                  {isDark ? "Mode clair" : "Mode sombre"}
                </button>
                <button
                  onClick={() => { switchLanguage(); setDrawerOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-base-content/60 hover:bg-base-200"
                >
                  {i18n.language === "fr" ? "🇫🇷 Français" : "🇬🇧 English"}
                </button>
                {user && (
                  <>
                    <Link
                      to="/profile"
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-base-content/60 hover:bg-base-200"
                      onClick={() => setDrawerOpen(false)}
                    >
                      <FaUserCircle className="text-base-content/40" /> {t("navbar.profile")}
                    </Link>
                    {canNotif && (
                      <Link
                        to="/notifications"
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-base-content/60 hover:bg-base-200"
                        onClick={() => setDrawerOpen(false)}
                      >
                        <FaBell className="text-base-content/40" />
                        {t("navbar.notifications") || "Notifications"}
                        {unreadCount > 0 && (
                          <span className="ml-auto w-5 h-5 bg-error text-error-content text-xs font-bold rounded-full flex items-center justify-center">
                            {unreadCount}
                          </span>
                        )}
                      </Link>
                    )}
                    <button
                      onClick={() => { logout(); setDrawerOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-error hover:bg-error/10"
                    >
                      <FaSignOutAlt /> {t("navbar.logout")}
                    </button>
                  </>
                )}
              </div>
            </aside>
          </div>
        )}

        {/* ── Main content ── */}
        <main className="flex-1 overflow-y-auto bg-gradient-to-b from-base-200 via-base-200 to-base-100">
          {impersonating && (
            <div className="bg-amber-500 text-black px-4 py-2 text-sm font-medium flex items-center justify-between gap-3 sticky top-0 z-40">
              <span className="flex items-center gap-2">
                <FaBuilding /> Mode superadmin — vous consultez « {impersonatedOrg?.name} » (org #{impersonatedOrg?.id})
              </span>
              <button onClick={stopImpersonation} className="btn btn-xs btn-neutral">Quitter</button>
            </div>
          )}
          <div className="p-4 sm:p-6 lg:px-8 max-w-[1600px] mx-auto">
            <Outlet />
          </div>

          {/* Footer plateforme (éditeur SaaS = The Movers), commun à toutes les orgs. */}
          <footer className="mt-6 border-t border-base-200 py-4 px-4 sm:px-8">
            <div className="max-w-[1600px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-base-content/40">
              <span>© {new Date().getFullYear()} The Movers · Plateforme de gestion de déménagement</span>
              <span className="flex items-center gap-4">
                <a href="mailto:contact@the-movers.eu" className="flex items-center gap-1.5 hover:text-primary transition-colors">
                  <FaEnvelope size={11} /> contact@the-movers.eu
                </a>
                <span className="opacity-60">{process.env.REACT_APP_VERSION || "dev"}</span>
              </span>
            </div>
          </footer>
        </main>
      </div>

    </div>
  );
}
